import { useState, useEffect, useRef } from 'react';
import { Table, DatePicker, Select, Button, Modal, Form, Input, Tag, message } from 'antd';
import { getData, createData, updateData, deleteData } from '../api/api';
import bgImage from '../assets/img/QrPage.png';
import { QRCodeCanvas } from 'qrcode.react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { SearchOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

const { Search } = Input;

const StaticQrs = ({ isAdmin, isMerchant }) => {
    const { t } = useTranslation(); // Translation hook
    const [data, setData] = useState([]);
    const [merchants, setMerchants] = useState([]);
    const [terminals, setTerminals] = useState([]);
    const [regions, setRegions] = useState([]);
    const [districts, setDistricts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
    const [filters, setFilters] = useState({});
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [form] = Form.useForm();
    const qrContainerRef = useRef(null);

    const downloadPDF = async (elementBox, filename) => {
        const pdf = new jsPDF('p', 'mm', 'a4');

        const img = new Image();
        img.src = bgImage;
        img.onload = async () => {
            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();
            pdf.addImage(img, 'JPEG', 0, 0, pageWidth, pageHeight);

            if (elementBox) {
                qrContainerRef.current = elementBox;
                const canvas = await html2canvas(qrContainerRef.current);
                const imgData = canvas.toDataURL('image/png');
                const qrSize = 100.5;
                const xPos = (pageWidth - qrSize) / 2;
                const yPos = (pageHeight - qrSize) / 2.45;
                pdf.addImage(imgData, 'PNG', xPos, yPos, qrSize, qrSize);
            }

            pdf.save(filename + '.pdf');
        };
    };

    const columns = [
        {
            title: t('staticQrs.index'),
            key: 'index',
            width: 60,
            render: (_, __, index) =>
                index + 1 + (pagination.current - 1) * pagination.pageSize,
        },
        { title: t('staticQrs.id'), dataIndex: 'id', key: 'id', width: 150 },
        { title: t('staticQrs.terminalType'), dataIndex: 'terminalType', key: 'terminalType', width: 150 },
        { title: t('staticQrs.merchantName'), dataIndex: 'merchantName', key: 'merchantName', width: 150 },
        { title: t('staticQrs.terminalName'), dataIndex: 'terminalName', key: 'terminalName', width: 150 },
        {
            title: t('staticQrs.qrCode'),
            dataIndex: 'link',
            key: 'link',
            width: 150,
            render: (link, record) => (
                <>
                    <Button
                        size="large"
                        type="primary"
                        onClick={async () => {
                            const element = document.getElementById(link);
                            if (!element) {
                                console.error(t('staticQrs.qrCodeNotFound'));
                                return;
                            }
                            await downloadPDF(element, record.id);
                        }}
                    >
                        {t('staticQrs.downloadQr')}
                    </Button>
                    <div
                        id={link}
                        style={{
                            display: 'flex',
                            position: 'absolute',
                            top: '-9999px',
                            left: '-9999px',
                            transform: 'translate(-50%, -50%)',
                            justifyContent: 'center',
                            alignItems: 'center',
                            width: '310px',
                            height: '310px',
                        }}
                    >
                        <QRCodeCanvas value={link} size={280} />
                    </div>
                </>
            ),
        },
        { title: t('staticQrs.redirectUrl'), dataIndex: 'redirectUrl', key: 'redirectUrl', width: 200 },
        {
            title: t('staticQrs.minAmount'),
            dataIndex: 'minAmount',
            key: 'minAmount',
            width: 100,
            render: (minAmount) => <p>{minAmount / 100} {t('staticQrs.currency')}</p>,
        },
        {
            title: t('staticQrs.maxAmount'),
            dataIndex: 'maxAmount',
            key: 'maxAmount',
            width: 100,
            render: (maxAmount) => <p>{maxAmount / 100} {t('staticQrs.currency')}</p>,
        },
        { title: t('staticQrs.createdAt'), dataIndex: 'createdAt', key: 'createdAt', width: 180 },
        { title: t('staticQrs.updatedAt'), dataIndex: 'updatedAt', key: 'updatedAt', width: 180 },
        {
            title: t('staticQrs.status'),
            dataIndex: 'status',
            key: 'status',
            width: 100,
            render: (status) =>
                status === 0 ? <Tag color="green">{t('staticQrs.active')}</Tag> : <Tag color="red">{t('staticQrs.inactive')}</Tag>,
        },
    ];

    // if (isAdmin) {
    //     columns.push({
    //         title: t('staticQrs.actions'),
    //         key: 'actions',
    //         width: 200,
    //         render: (_, record) => (
    //             <>
    //                 <Button size="small" onClick={() => handleUpdate(record)}>
    //                     {t('staticQrs.edit')}
    //                 </Button>
    //                 <Button
    //                     size="small"
    //                     danger
    //                     onClick={() => handleDelete(record.id)}
    //                     style={{ marginLeft: 8 }}
    //                 >
    //                     {t('staticQrs.delete')}
    //                 </Button>
    //             </>
    //         ),
    //     });
    // }

    const fetchData = async () => {
        setLoading(true);
        try {
            const response = await getData(
                isAdmin
                    ? '/admin/dashboard/static-qr-list-by-filter'
                    : isMerchant
                        ? '/merchant/dashboard/static-qr-list-by-filter'
                        : '/merchant-user/dashboard/static-qr-list-by-filter',
                {
                    page: pagination.current - 1,
                    size: pagination.pageSize,
                    status: -1,
                    ...filters,
                }
            );
            setData(response.data?.data?.content || []);
            setPagination({
                ...pagination,
                total: response.data?.data?.totalCount,
            });
        } catch (error) {
            console.error(error);
            message.error(t('staticQrs.dataLoadError'));
        }
        setLoading(false);
    };

    const getMerchants = async () => {
        try {
            const response = await getData('/admin/dashboard/merchants-list-by-filter', {
                page: 0,
                size: 1000000,
                status: -1,
            });
            setMerchants(response.data?.data?.content || []);
        } catch (error) {
            console.error(error);
            message.error(t('staticQrs.merchantsLoadError'));
        }
    };

    const getTerminals = async () => {
        const merchantId = filters.merchantId;
        if (!isAdmin || merchantId) {
            try {
                const response = await getData(
                    isAdmin
                        ? '/admin/dashboard/terminal-list-by-filter'
                        : '/merchant/dashboard/terminal-list-by-filter',
                    {
                        page: 0,
                        size: 10000000,
                        status: -1,
                        merchantId: merchantId,
                    }
                );
                setTerminals(response.data?.data?.content || []);
            } catch (error) {
                console.error(error);
                message.error(t('staticQrs.terminalsLoadError'));
            }
        } else {
            setTerminals([]);
        }
    };

    const getRegions = async () => {
        try {
            const response = await getData('/admin/dashboard/region/list', null);
            console.log(response)
            setRegions(response.data?.data || []);
        } catch (error) {
            console.error(error);
            // message.error(t('terminals.merchantsLoadError'));
        }
    };

    const getDistricts = async () => {
        const rId =  filters.regionId;
        if (rId){
            try {
                const response = await getData('/admin/dashboard/district/list/by-region-id', {
                    regionId:rId
                });
                setDistricts(response.data?.data || []);
            } catch (error) {
                console.error(error);
                // message.error(t('terminals.merchantsLoadError'));
            }
        }
    };

    useEffect(() => {
        fetchData();
        if (isAdmin && (!merchants || merchants.length === 0)) {
            getMerchants();
        }
        if (isAdmin && (!regions || regions.length === 0)) {
            getRegions();
        }
    }, [pagination.current, pagination.pageSize, filters]);

    useEffect(() => {
        if (isAdmin || isMerchant) {
            getTerminals();
        }
        getDistricts()
    }, [filters.merchantId, filters.regionId]);

    const handleTableChange = (newPagination) => {
        setPagination(newPagination);
    };

    const handleFilter = (values) => {
        setFilters(values);
        setPagination({ ...pagination, current: 1 });
    };

    const handleCreate = async (values) => {
        try {
            const response = await createData('/admin/static-qr/default-create', {
                ...values,
                minAmount: values.minAmount * 100,
                maxAmount: values.maxAmount * 100,
            });
            if (response?.data?.success && response?.data?.data) {
                message.success(t('staticQrs.successAdd'));
                fetchData();
                handleCancel();
            } else {
                alert(response?.data?.error?.text || t('staticQrs.errorAdd'));
                message.error(t('staticQrs.errorAdd'));
            }
        } catch (error) {
            console.error(error);
            alert(t('staticQrs.errorAdd'));
            message.error(t('staticQrs.errorAdd'));
        }
    };

    const handleUpdate = async (record) => {
        // await updateData('/dashboard', record.id, record);
        // fetchData();
    };

    const handleDelete = async (id) => {
        // await deleteData('/dashboard', id);
        // fetchData();
    };

    const showModal = () => {
        setIsModalVisible(true);
    };

    const handleCancel = () => {
        setIsModalVisible(false);
        form.resetFields();
    };

    return (
        <div>
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 16,
                }}
            >
                <h2 style={{ margin: 0 }}>{t('staticQrs.title')}</h2>
                {isAdmin ? (
                    <Button type="primary" onClick={showModal}>
                        {t('staticQrs.add')}
                    </Button>
                ) : (
                    ''
                )}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
                {isAdmin ? (
                    <Select
                        style={{ width: 200 }}
                        defaultValue={null}
                        onChange={(value) => handleFilter({ ...filters, merchantId: value })}
                        size="middle"
                        placeholder={t('staticQrs.selectMerchant')}
                        showSearch
                        filterOption={(input, option) =>
                            option?.children?.toLowerCase().includes(input.toLowerCase())
                        }
                        allowClear
                        notFoundContent={t('staticQrs.notFound')}
                        dropdownStyle={{ zIndex: 9999 }}
                        getPopupContainer={(trigger) => trigger.parentNode}
                    >
                        <Select.Option value={null}>{t('staticQrs.allMerchants')}</Select.Option>
                        {merchants?.map((value) => (
                            <Select.Option key={value.id} value={value.id}>
                                {value.name}
                            </Select.Option>
                        ))}
                    </Select>
                ) : (
                    ''
                )}

                {isAdmin || isMerchant ? (
                    <Select
                        style={{ width: 200 }}
                        defaultValue={null}
                        onChange={(value) => handleFilter({ ...filters, terminalId: value })}
                        size="middle"
                        placeholder={t('staticQrs.selectTerminal')}
                        showSearch
                        filterOption={(input, option) =>
                            option?.children?.toLowerCase().includes(input.toLowerCase())
                        }
                        allowClear
                        notFoundContent={t('staticQrs.notFound')}
                        dropdownStyle={{ zIndex: 9999 }}
                        getPopupContainer={(trigger) => trigger.parentNode}
                        disabled={isAdmin ? !filters.merchantId : false}
                    >
                        {terminals?.map((value) => (
                            <Select.Option key={value.id} value={value.id}>
                                {value.name}
                            </Select.Option>
                        ))}
                    </Select>
                ) : (
                    ''
                )}

                {
                    isAdmin ?
                        <Select
                            style={{ width: 200 }}
                            defaultValue={null}
                            onChange={(value) => handleFilter({ ...filters, regionId: value })}
                            size="middle"
                            placeholder={"Regions"}
                            showSearch
                            filterOption={(input, option) =>
                                option?.children?.toLowerCase().includes(input.toLowerCase())
                            }
                            allowClear
                            notFoundContent={"Not found"}
                            dropdownStyle={{ zIndex: 9999 }}
                            getPopupContainer={(trigger) => trigger.parentNode}
                        >
                            {regions?.map((value) => (
                                <Select.Option key={value.id} value={value.id}>
                                    {value.nameRu}
                                </Select.Option>
                            ))}
                        </Select>:' '

                }

                {
                    isAdmin ?
                        <Select
                            style={{ width: 200 }}
                            defaultValue={null}
                            onChange={(value) => handleFilter({ ...filters, districtId: value })}
                            size="middle"
                            placeholder={"Districts"}
                            showSearch
                            filterOption={(input, option) =>
                                option?.children?.toLowerCase().includes(input.toLowerCase())
                            }
                            allowClear
                            notFoundContent={"Not found"}
                            dropdownStyle={{ zIndex: 9999 }}
                            getPopupContainer={(trigger) => trigger.parentNode}
                            disabled={!filters.regionId}
                        >
                            {districts?.map((value) => (
                                <Select.Option key={value.id} value={value.id}>
                                    {value.nameRu}
                                </Select.Option>
                            ))}
                        </Select>:' '

                }

                <Search
                    placeholder={t('staticQrs.searchPlaceholder')}
                    allowClear
                    enterButton={<SearchOutlined />}
                    size="middle"
                    style={{ width: 300 }}
                    onSearch={(value) => handleFilter({ ...filters, search: value })}
                />
            </div>
            <Table
                columns={columns}
                dataSource={data}
                pagination={{
                    ...pagination,
                    showTotal: (total) => t('staticQrs.total', { total }),
                }}
                loading={loading}
                onChange={handleTableChange}
                rowKey="id"
                scroll={{ x: 'max-content' }}
            />
            <Modal
                title={t('staticQrs.addTitle')}
                visible={isModalVisible}
                onCancel={handleCancel}
                footer={null}
            >
                <Form form={form} onFinish={handleCreate} layout="vertical">
                    <Form.Item
                        label={t('staticQrs.redirectUrl')}
                        name="redirectUrl"
                        rules={[{ required: true, message: t('staticQrs.redirectUrlRequired') }]}
                        initialValue="https://qrpay.uz"
                    >
                        <Input />
                    </Form.Item>

                    <Form.Item
                        label={t('staticQrs.minAmount')}
                        name="minAmount"
                        rules={[{ required: true, message: t('staticQrs.minAmountRequired') }]}
                        initialValue={1000}
                    >
                        <Input type="number" />
                    </Form.Item>

                    <Form.Item
                        label={t('staticQrs.maxAmount')}
                        name="maxAmount"
                        rules={[{ required: true, message: t('staticQrs.maxAmountRequired') }]}
                        initialValue={50000000}
                    >
                        <Input type="number" />
                    </Form.Item>

                    <Form.Item>
                        <Button type="primary" htmlType="submit">
                            {t('staticQrs.add')}
                        </Button>
                        <Button style={{ marginLeft: 8 }} onClick={handleCancel}>
                            {t('staticQrs.cancel')}
                        </Button>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default StaticQrs;